import { Grid, LinearProgress, makeStyles, Toolbar, Typography } from "@material-ui/core";
import { useQuery } from "@redux-requests/react";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { toArray } from "lodash";
import { BaseLayout, Header, Loader } from "../components";
import EPostersGrid from "../components/EPostersGrid";
import AutocompleteField from "../components/formik/AutocompleteField";
import MultipleAutocompleteField from "../components/formik/MultipleAutocompleteField";
import TextField from "../components/formik/TextField";
import { getEPosters, getEPostersInternalIds, getProfiles, getTags } from "../ducks/actions";
import * as types from "../ducks/types";
import { useQueryParams } from "../hooks";
import { ReactComponent as EposterSvg } from "../assets/icons/eposters.svg";

const useStyles = makeStyles(theme => ({
  root: {
    "& > *": {
      marginBottom: theme.spacing(3)
    }
  },
  filter: {
    marginLeft: theme.spacing(1),
    flexGrow: 0.2
  },
  infiniteScroll: {
    overflow: "hidden !important"
  }
}));

const EPostersPage = () => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const [{ tags, profileId, title, internalId }, mergeParams] = useQueryParams();

  const [perPage] = useState(24);
  const [eposterPage, setEposterPage] = useState(1);

  useEffect(() => {
    dispatch(getEPosters({ tags, profileId, title, internalId, page: eposterPage, perPage }));
  }, [dispatch, profileId, title, internalId, eposterPage, perPage, tags]);

  useEffect(() => {
    dispatch(getTags({ resource: "eposters" }));
    dispatch(getProfiles({ role: "accademic" }));
    dispatch(getEPostersInternalIds());
  }, [dispatch]);

  const {
    data: {
      data: ePosters,
      meta: { total: totalEposters }
    },
    loading: ePostersLoading
  } = useQuery({ type: types.GET_EPOSTERS, defaultData: { data: [], meta: { total: 0 } } });

  const {
    data: { data: profiles },
    loading: profilesLoading
  } = useQuery({ type: types.GET_PROFILES, defaultData: { data: [] } });

  const {
    data: { data: availableTags },
    loading: tagsLoading
  } = useQuery({ type: types.GET_TAGS, defaultData: { data: [] } });

  const { data: epostersInternalIds, loading: ePostersInternalIdsLoading } = useQuery({
    type: types.GET_EPOSTERS_INTERNAL_IDS,
    defaultData: { data: [] }
  });

  const profileOptions = profiles.map(({ id, firstName, lastName }) => ({
    label: `${lastName} ${firstName}`,
    value: id
  }));

  const tagOptions = availableTags.map(({ name }) => ({
    label: name,
    value: name
  }));

  const internalIdOptions = toArray(epostersInternalIds).map(({ internal_id: internalId }) => ({
    label: internalId,
    value: internalId
  }));

  const onSubmit = ({ tags, profileId, title, internalId }) => {
    setEposterPage(1);
    mergeParams({ tags, profileId, title, internalId });
  };

  const nextEposterPage = () => {
    setEposterPage(parseInt(eposterPage, 10) + 1);
  };

  return (
    <BaseLayout>
      <InfiniteScroll
        className={classes.infiniteScroll}
        scrollableTarget="scrollerContent"
        dataLength={ePosters.length}
        next={nextEposterPage}
        hasMore={ePosters.length < totalEposters}
        loader={
          <>
            <LinearProgress />
            <br />
          </>
        }
        endMessage={
          <p style={{ textAlign: "center" }}>
            <b>
              {intl.formatMessage({
                id: "Pages.EPostersPage.InfiniteScrollEnd",
                defaultMessage: "All e-posters are shown!"
              })}
            </b>
          </p>
        }
      >
        <div className={classes.root}>
          <Header
            icon={EposterSvg}
            title={intl.formatMessage({
              id: "Pages.EPostersPage.EPosters",
              defaultMessage: "e-Posters"
            })}
          />
          <Formik
            initialValues={{
              tags: tags || [],
              profileId: profileId || "",
              title: title || "",
              internalId: internalId || ""
            }}
            onSubmit={onSubmit}
          >
            <Toolbar>
              <Loader loading={profilesLoading || tagsLoading || ePostersInternalIdsLoading}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={2} lg={1}>
                    <Typography variant="subtitle1">
                      <strong>
                        {intl.formatMessage({
                          id: "Pages.EPostersPage.FilterBy",
                          defaultMessage: "Filter by"
                        })}
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={5} lg={3}>
                    <MultipleAutocompleteField
                      name="tags"
                      variant="outlined"
                      label={intl.formatMessage({
                        id: "Pages.EPostersPage.Tag",
                        defaultMessage: "Tag"
                      })}
                      className={classes.filter}
                      options={tagOptions}
                      submitOnChange
                    />
                  </Grid>
                  <Grid item xs={12} sm={5} lg={3}>
                    <AutocompleteField
                      name="profileId"
                      variant="outlined"
                      label={intl.formatMessage({
                        id: "Pages.EPostersPage.Author",
                        defaultMessage: "Author"
                      })}
                      className={classes.filter}
                      options={profileOptions}
                      submitOnChange
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <TextField
                      name="title"
                      variant="outlined"
                      label={intl.formatMessage({
                        id: "Pages.EPostersPage.Title",
                        defaultMessage: "Title"
                      })}
                      className={classes.filter}
                      submitOnChange
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={2}>
                    <AutocompleteField
                      name="internalId"
                      variant="outlined"
                      label={intl.formatMessage({
                        id: "Pages.EPostersPage.Id",
                        defaultMessage: "Id"
                      })}
                      className={classes.filter}
                      options={internalIdOptions}
                      submitOnChange
                    />
                  </Grid>
                </Grid>
              </Loader>
            </Toolbar>
          </Formik>
          <EPostersGrid loading={ePostersLoading} ePosters={ePosters} />
        </div>
      </InfiniteScroll>
    </BaseLayout>
  );
};

export default EPostersPage;
